import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import produce from 'immer';
import React, {useState, useMemo, useEffect} from 'react';
import {v4 as uuidv4} from 'uuid';

import {assert} from 'Asserts';
import WorkspaceItemInaccessibleGuard from 'auth/WorkspaceItemInaccessibleGuard';
import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import {
  ActionMethod,
  ActionOrigin,
  ProcessTaskStatusType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  FetchSelectorsByIdQuery,
  FetchSelectorsByIdQueryVariables,
} from 'notebook/structured-tables/hooks/__generated/FetchSelectorsByIdQuery';
import {FETCH_SELECTORS_BY_ID_QUERY} from 'notebook/structured-tables/hooks/useSelectorsById';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import {UnknownStructuredTable} from 'notebook/structured-tables/StructuredTableConstants';
import {StructuredTableKind} from 'notebook/structured-tables/StructuredTableConstants';
import StructuredTableOutsideNotebook from 'notebook/structured-tables/StructuredTableOutsideNotebook';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {MaximumTableHeight} from 'notebook/tables/constants/TableLayoutConstants';
import doesEvaluatedTableHaveAnyErrors from 'notebook/tables/errors/doesEvaluatedTableHaveAnyErrors';
import {EvaluatedTable} from 'notebook/tables/excess/EvaluationConstants';
import notifications from 'notifications';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  PROCESSES_DIRECT_EXECUTE_STARTED_EVENT,
  createProcessEventMetadata,
  PROCESSES_DIRECT_EXECUTE_ENDED_EVENT,
} from 'performance/processProcessesEvents';
import {ProcessTaskStatus} from 'processes/__generated/ProcessTaskStatus';
import * as ProcessManagementDAO from 'processes/dao/ProcessManagementDAO';
import {
  DefaultStatusDisplayName,
  EVALUATED_TABLE_ERROR_OPTIONS,
  ProcessTableLocation,
} from 'processes/ProcessConstants';
import {invalidateInDashboard} from 'processes/ProcessManagementActions';
import {findValidOutStatuses} from 'processes/status/TaskStatusUtil';
import {createOutputTableWithEmptyRows} from 'processes/tables/createOutputTable';
import {createTaskTableFromTasks} from 'processes/tables/createTaskTable';
import {outputTableToCreateDirectExecutionOutputs} from 'processes/tables/outputTableToOutputsUtils';
import {ProcessOutputStructuredTable} from 'processes/tables/ProcessOutputStructuredTableAdapter';
import ProcessOutputStructuredTableAdapter from 'processes/tables/ProcessOutputStructuredTableAdapter';
import ProcessTaskStructuredTableAdapter, {
  ProcessTaskStructuredTable,
  getTaskSchema,
} from 'processes/tables/ProcessTaskStructuredTableAdapter';
import taskTableToTasks from 'processes/tables/taskTableToTasks';
import {ButtonStyle} from 'ui-library/buttons/Button';
import useOnMount from 'ui-library/hooks/useOnMount';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {ExpandableModal} from 'ui-library/modals/ExpandableModal';
import {ModalButton} from 'ui-library/modals/Modal';
import StepNav, {StepItemStatus, StepNavVariant} from 'ui-library/StepNav';
import * as modals from 'util/Modals';
import {withLoadingScreen} from 'util/Promises';

import {InaccessibleInfoGraphQL} from '../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';
import {ProcessOutputGraphQL} from '../../../graphql-fragments/processes/__generated/ProcessOutputGraphQL';
import {ProcessTaskGraphQL} from '../../../graphql-fragments/processes/__generated/ProcessTaskGraphQL';
import {SchemaFieldSelectorGraphQL} from '../../../graphql-fragments/schemas/__generated/SchemaFieldSelectorGraphQL';
import {
  DirectExecuteTasksQuery,
  DirectExecuteTasksQueryVariables,
} from './__generated/DirectExecuteTasksQuery';

type ProcessTask = ExtractByTypename<DirectExecuteTasksQuery['processTasks'][number], 'ProcessTaskNode'>;

export const DIRECT_EXECUTION_TASKS_QUERY = gql`
  query DirectExecuteTasksQuery($processTaskIds: [StrictString!]!) @skipAudit @engComponent(name: WORKFLOWS) {
    processTasks(ids: $processTaskIds) {
      ... on ProcessTaskNode {
        ...ProcessTaskGraphQL
        schema {
          ...ProcessTaskSchemaGraphQL
          statusLifecycle {
            ...ProcessTaskStatusLifecycleGraphQL
          }
        }
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('ProcessTask').fragment}
  ${getFragmentWrapper('ProcessTaskSchema').fragment}
  ${getFragmentWrapper('ProcessTaskStatusLifecycle').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface SelectorsById {
  [id: string]: SchemaFieldSelectorGraphQL;
}

interface DirectExecutionModalPropsWithData {
  isLoading: false;
  tasks: Array<ProcessTask>;
  directExecutionUuid: string;
  selectorsById: SelectorsById;
  autopopulateFromTasks?: boolean;
  onRequestClose: () => void;
  onTasksOrOutputsChange?: (tasks: Array<ProcessTaskGraphQL>, outputs?: Array<ProcessOutputGraphQL>) => void;
  onCancelExecution?: (tasks: Array<ProcessTaskGraphQL>) => void;
}

interface DirectExecutionModalPropsStillLoading {
  isLoading: true;
  tasks?: never;
  directExecutionUuid?: never;
  selectorsById?: never;
  onRequestClose: () => void;
  onTasksOrOutputsChange?: (tasks: Array<ProcessTaskGraphQL>, outputs?: Array<ProcessOutputGraphQL>) => void;
  onCancelExecution?: (tasks: Array<ProcessTaskGraphQL>) => void;
}

type DirectExecutionModalProps = DirectExecutionModalPropsWithData | DirectExecutionModalPropsStillLoading;

enum DirectExecutionStepKey {
  CREATE_OUTPUTS = 'CREATE_OUTPUTS',
  FINALIZE_TASKS = 'FINALIZE_TASKS',
}

const DIRECT_EXECUTION_PANES = [
  {
    key: DirectExecutionStepKey.CREATE_OUTPUTS,
    title: 'Create outputs',
  },
  {
    key: DirectExecutionStepKey.FINALIZE_TASKS,
    title: 'Submit outputs and update task status',
  },
];

const DEFAULT_STATUS_BY_STEP = {
  [DirectExecutionStepKey.CREATE_OUTPUTS]: StepItemStatus.UNVISITED,
  [DirectExecutionStepKey.FINALIZE_TASKS]: StepItemStatus.UNVISITED,
};

function lockTable<T extends UnknownStructuredTable>(structuredTable: T, except?: Array<TableColumnType>): T {
  const colsToIgnore = except ?? [];
  return produce(structuredTable, (draftStructuredTable) => {
    draftStructuredTable.columns = draftStructuredTable.columns.map((col) =>
      colsToIgnore.includes(col.columnType) ? col : {...col, isReadOnly: true}
    );
  });
}

interface BaseDirectExecutionPaneProps {
  taskTable: ProcessTaskStructuredTable;
  outputTable: ProcessOutputStructuredTable;
  onComputedTaskTableUpdate: (computedTable: EvaluatedTable) => void;
  onComputedOutputTableUpdate: (computedTable: EvaluatedTable) => void;
}

export function CreateOutputsPane(
  props: {
    onOutputTableChange: (structuredTable: ProcessOutputStructuredTable) => void;
  } & BaseDirectExecutionPaneProps
): JSX.Element {
  const taskTable = useMemo(() => lockTable(props.taskTable), [props.taskTable]);
  const taskIds = useMemo(() => {
    const taskTableAdapter = getStructuredTableAdapters()[
      StructuredTableKind.PROCESS_TASK
    ] as ProcessTaskStructuredTableAdapter;
    return taskTableAdapter.getAllTaskIds(props.taskTable);
  }, [props.taskTable]);
  return (
    <>
      <StructuredTableOutsideNotebook
        cid={taskTable.cid}
        locked={true}
        structuredTable={taskTable}
        updateHandler={() => {}}
        onComputedTableChanged={props.onComputedTaskTableUpdate}
        enableFormulas={true}
        maximumHeight={MaximumTableHeight.AUTO_FIXED}
      />
      <StructuredTableOutsideNotebook
        cid={props.outputTable.cid}
        structuredTable={props.outputTable}
        updateHandler={(structuredTable: ProcessOutputStructuredTable) => {
          props.onOutputTableChange(structuredTable);
        }}
        onComputedTableChanged={props.onComputedOutputTableUpdate}
        outsideNotebookContext={{
          taskIds,
        }}
        enableFormulas={true}
        maximumHeight={MaximumTableHeight.AUTO_FIXED}
      />
    </>
  );
}

export function FinalizeTasksPane(
  props: {
    computedTaskTable: EvaluatedTable;
    computedOutputTable: EvaluatedTable;
    onTaskTableChange: (structuredTable: ProcessTaskStructuredTable) => void;
  } & BaseDirectExecutionPaneProps
): JSX.Element {
  const taskTable = useMemo(
    () => lockTable(props.taskTable, [TableColumnType.PROCESS_TASK_STATUS]),
    [props.taskTable]
  );
  const outputTable = useMemo(() => lockTable(props.outputTable), [props.outputTable]);

  useOnMount(() => {
    const taskTableAdapter = getStructuredTableAdapters()[
      StructuredTableKind.PROCESS_TASK
    ] as ProcessTaskStructuredTableAdapter;
    const outputTableAdapter = getStructuredTableAdapters()[
      StructuredTableKind.PROCESS_OUTPUT
    ] as ProcessOutputStructuredTableAdapter;
    const taskIdsWithOutputs = new Set(
      outputTableAdapter.getAllTaskIds(outputTable.columns, props.computedOutputTable)
    );
    const taskSchema = getTaskSchema(props.taskTable.schemas);
    assert(taskSchema != null, 'Task schema cannot be null');
    const newRows = props.taskTable.rows.map((row) => {
      const taskId = taskTableAdapter.getTaskId(props.taskTable.columns, row);
      const status = taskTableAdapter.getStatus(props.taskTable.columns, row) as ProcessTaskStatus | null;
      assert(taskId != null, 'Expected tasks to all be finalized');
      assert(status != null, 'Expected task table to have status');
      if (status.statusType !== ProcessTaskStatusType.IN_PROGRESS) {
        return {...row};
      }
      const nextStatuses = findValidOutStatuses(status, taskSchema.statusLifecycle);
      const typeToFind = taskIdsWithOutputs.has(taskId)
        ? ProcessTaskStatusType.COMPLETED
        : ProcessTaskStatusType.FAILED;
      const nextStatus = nextStatuses.find(({statusType}) => statusType === typeToFind) ?? null;
      if (nextStatus == null) {
        return {...row};
      }
      return taskTableAdapter.getRowWithNewStatus(props.taskTable.columns, row, nextStatus);
    });
    props.onTaskTableChange({...props.taskTable, rows: newRows});
  });

  return (
    <>
      <div className={css(styles.modalText)}>
        Tasks will be updated to a terminal status upon confirmation. You won't be able to further modify task
        statuses or create additional outputs for these tasks after submitting this update.
        <br />
        <br />
        Any outputs or tasks that are part of a flowchart will be mapped to the next step when the task status
        is set to {DefaultStatusDisplayName.COMPLETED}.
      </div>
      <StructuredTableOutsideNotebook
        cid={props.taskTable.cid}
        structuredTable={taskTable}
        updateHandler={(structuredTable: ProcessTaskStructuredTable) => {
          props.onTaskTableChange(structuredTable);
        }}
        onComputedTableChanged={props.onComputedTaskTableUpdate}
        enableFormulas={true}
        maximumHeight={MaximumTableHeight.AUTO_FIXED}
      />
      <StructuredTableOutsideNotebook
        cid={outputTable.cid}
        locked={true}
        structuredTable={outputTable}
        updateHandler={() => {}}
        onComputedTableChanged={props.onComputedOutputTableUpdate}
        enableFormulas={true}
        maximumHeight={MaximumTableHeight.AUTO_FIXED}
      />
    </>
  );
}

export function DirectExecutionModal(props: DirectExecutionModalProps): JSX.Element {
  const {isLoading} = props;
  const [currentStepIdx, setCurrentStepIdx] = useState(0);
  const getTaskTable = (): ProcessTaskStructuredTable | null =>
    isLoading
      ? null
      : createTaskTableFromTasks({
          tasks: props.tasks,
          selectorsById: props.selectorsById,
          tableLocation: ProcessTableLocation.DIRECT_EXECUTION_MODAL,
          locked: false,
        });
  const getOutputTable = (): ProcessOutputStructuredTable | null =>
    isLoading
      ? null
      : createOutputTableWithEmptyRows({
          ...(props.autopopulateFromTasks ? {sourceTasks: props.tasks} : {numRows: props.tasks.length}),
          outputSchema: props.tasks[0].schema.outputSchema!,
          metadata: {tableLocation: ProcessTableLocation.DIRECT_EXECUTION_MODAL},
          selectorsById: props.selectorsById,
        });
  const [taskTable, setTaskTable] = useState<ProcessTaskStructuredTable | null>(getTaskTable());
  const [computedTaskTable, setComputedTaskTable] = useState<EvaluatedTable>();
  const [outputTable, setOutputTable] = useState<ProcessOutputStructuredTable | null>(getOutputTable());
  const [computedOutputTable, setComputedOutputTable] = useState<EvaluatedTable>();

  const areTablesValid = useMemo(
    () =>
      computedTaskTable != null &&
      computedOutputTable != null &&
      !doesEvaluatedTableHaveAnyErrors(computedTaskTable, EVALUATED_TABLE_ERROR_OPTIONS) &&
      !doesEvaluatedTableHaveAnyErrors(computedOutputTable, EVALUATED_TABLE_ERROR_OPTIONS),
    [computedTaskTable, computedOutputTable]
  );

  useEffect(() => {
    setTaskTable(getTaskTable());
    setOutputTable(getOutputTable());
    // "isLoading" is intentional in the dependency list, to force a refresh when the queries are ready
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const currentPane = DIRECT_EXECUTION_PANES[currentStepIdx].key;

  const getModalBody = (): JSX.Element => {
    if (isLoading || !taskTable || !outputTable) {
      return <CenteredHorizontalThrobber />;
    }
    switch (currentPane) {
      case DirectExecutionStepKey.CREATE_OUTPUTS:
        return (
          <CreateOutputsPane
            taskTable={taskTable}
            outputTable={outputTable}
            onOutputTableChange={setOutputTable}
            onComputedTaskTableUpdate={setComputedTaskTable}
            onComputedOutputTableUpdate={setComputedOutputTable}
          />
        );
      case DirectExecutionStepKey.FINALIZE_TASKS:
        return (
          <FinalizeTasksPane
            taskTable={taskTable}
            outputTable={outputTable}
            computedTaskTable={computedTaskTable!}
            computedOutputTable={computedOutputTable!}
            onTaskTableChange={setTaskTable}
            onComputedTaskTableUpdate={setComputedTaskTable}
            onComputedOutputTableUpdate={setComputedOutputTable}
          />
        );
      default:
        assert(false, 'Not supported');
    }
  };

  const getModalButtons = (): Array<ModalButton> => {
    switch (currentPane) {
      case DirectExecutionStepKey.CREATE_OUTPUTS:
        return [
          {
            key: 'next',
            text: 'Continue',
            disabled: !areTablesValid,
            style: ButtonStyle.PRIMARY,
            onClick: () => {
              setCurrentStepIdx(currentStepIdx + 1);
            },
          },
        ];
      case DirectExecutionStepKey.FINALIZE_TASKS:
        return [
          {
            key: 'back',
            text: 'Back',
            onClick: () => {
              setCurrentStepIdx(currentStepIdx - 1);
            },
          },
          {
            key: 'submit',
            text: 'Submit',
            disabled: !areTablesValid || isLoading,
            submit: true,
            onClick: async () => {
              const actionOrigin = ActionOrigin.WORKFLOWS_DIRECT_EXECUTION_MODAL_WORKFLOW_TASK_TABLE;

              const taskSpecs = (
                taskTableToTasks({
                  table: taskTable!,
                  evaluatedTable: computedTaskTable!,
                  sourceType: 'dashboard',
                  actionMethod: ActionMethod.BUTTON_CLICK,
                  actionOrigin,
                }) as Array<ProcessManagementDAO.EditExistingTask>
              ).map(({id, sourceType, statusId}) => ({
                id,
                sourceType,
                statusId: statusId!,
              }));
              const outputSpecs = outputTableToCreateDirectExecutionOutputs({
                table: outputTable!,
                evaluatedTable: computedOutputTable!,
                sourceType: 'dashboard',
              });

              try {
                const syncThreshold =
                  BenchlingConfig.WORKFLOWS_MAX_TASKS_FOR_SYNCHRONOUS_DIRECT_TASK_COMPLETION;

                const profileEventMetadata = createProcessEventMetadata({
                  numItems: taskSpecs.length + outputSpecs.length,
                  numTasks: taskSpecs.length,
                  numOutputs: outputSpecs.length,
                });
                const profileEventId = uuidv4();

                logProfilerTimingEvent(
                  {...PROCESSES_DIRECT_EXECUTE_STARTED_EVENT, id: profileEventId},
                  profileEventMetadata
                );

                const {tasks, outputs} = await (syncThreshold === -1 || outputSpecs.length <= syncThreshold
                  ? ProcessManagementDAO.completeDirectExecution({
                      tasks: taskSpecs,
                      outputs: outputSpecs,
                      directExecutionUuid: props.directExecutionUuid!,
                    })
                  : ProcessManagementDAO.completeDirectExecutionAsync({
                      tasks: taskSpecs,
                      outputs: outputSpecs,
                      directExecutionUuid: props.directExecutionUuid!,
                    }));

                invalidateInDashboard(tasks);
                props.onTasksOrOutputsChange?.(tasks, outputs);
                notifications.success(`Successfully executed ${pluralize(tasks.length, 'task')}`);
                props.onRequestClose();

                logProfilerTimingEvent(
                  {...PROCESSES_DIRECT_EXECUTE_ENDED_EVENT, id: profileEventId},
                  profileEventMetadata
                );
                // eslint-disable-next-line benchlint/always-throw-from-catch
              } catch (e) {
                notifications.notifyError(e);
              }
            },
          },
        ];
      default:
        assert(false, `Invalid step ${currentStepIdx}`);
    }
  };

  const onRequestClose = async (): Promise<void> => {
    if (isLoading) {
      // The user can click the X button while the data are still loading. This early return
      // just nopes their click, without closing the modal.
      return;
    }

    const confirmed = await modals.confirm(
      'Undo execution',
      'Tasks will return to pending or planned status.',
      ButtonStyle.PRIMARY,
      {
        promiseCompatible: true,
        enableRedesign: true,
      }
    );
    if (!confirmed) {
      return;
    }
    try {
      withLoadingScreen(
        (async () => {
          const {tasks} = await ProcessManagementDAO.undoDirectExecution({
            taskIds: props.tasks.map(({id}) => id),
            directExecutionUuid: props.directExecutionUuid,
          });
          const processes = [...new Set(tasks.map(({process}) => process.id))].map((id) => ({id}));
          invalidateInDashboard([...tasks, ...processes]);
          props.onTasksOrOutputsChange?.(tasks);
        })(),
        'Cancelling task execution'
      );
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (e) {
      notifications.notifyError(e);
    }
    props.onRequestClose();
  };

  return (
    <ExpandableModal
      title="Execute tasks"
      expandHeight={true}
      large={true}
      onRequestClose={onRequestClose}
      buttons={getModalButtons()}
      modalKey="directExecution"
      rememberExpandedSetting={true}
    >
      <div className={css(styles.stepNavContainer)}>
        <StepNav
          steps={DIRECT_EXECUTION_PANES}
          statusByStep={DEFAULT_STATUS_BY_STEP}
          currentStepKey={DIRECT_EXECUTION_PANES[currentStepIdx].key}
          onUpdateCurrentStep={() => {}}
          variant={StepNavVariant.FLAT}
        />
      </div>
      <div className={css(styles.modalBody)}>{getModalBody()}</div>
    </ExpandableModal>
  );
}

interface DirectExecutionModalWithErrorProps {
  errorMessage: string;
  onRequestClose: () => void;
}
function DirectExecutionModalWithError(props: DirectExecutionModalWithErrorProps): JSX.Element {
  return (
    <ExpandableModal
      title="Execute tasks"
      expandHeight={true}
      large={true}
      onRequestClose={props.onRequestClose}
      buttons={[
        {
          key: 'close',
          text: 'Close',
          submit: true,
          onClick: props.onRequestClose,
        },
      ]}
      modalKey="directExecution"
      rememberExpandedSetting={true}
    >
      <div className={css(styles.stepNavContainer)}>
        <StepNav
          steps={DIRECT_EXECUTION_PANES}
          statusByStep={DEFAULT_STATUS_BY_STEP}
          currentStepKey={DIRECT_EXECUTION_PANES[0].key}
          onUpdateCurrentStep={() => {}}
          variant={StepNavVariant.FLAT}
        />
      </div>
      <div className={css(styles.modalBody)}>
        <p>{props.errorMessage}</p>
      </div>
    </ExpandableModal>
  );
}

export interface QueryingDirectExecutionModalProps {
  taskIds: Array<string>;
  directExecutionUuid: string;
  onRequestClose: () => void;
  onTasksOrOutputsChange?: (tasks: Array<ProcessTaskGraphQL>, outputs?: Array<ProcessOutputGraphQL>) => void;
}

export default function QueryingDirectExecutionModal(props: QueryingDirectExecutionModalProps): JSX.Element {
  const selectorQueryResults = useBenchlingQuery<FetchSelectorsByIdQuery, FetchSelectorsByIdQueryVariables>(
    FETCH_SELECTORS_BY_ID_QUERY,
    {
      fetchPolicy: 'network-only',
      variables: {includeArchived: true},
    }
  );
  const tasksQueryResults = useBenchlingQuery<DirectExecuteTasksQuery, DirectExecuteTasksQueryVariables>(
    DIRECT_EXECUTION_TASKS_QUERY,
    // When this query returns, it may cause other queries on the page to be invalidated which will dirty the
    // cache and cause this query to refetch. So we set the nextFetchPolicy to cache-only to just read data
    // from the cache after we've fetched it once from the API.
    {variables: {processTaskIds: props.taskIds}, fetchPolicy: 'network-only', nextFetchPolicy: 'cache-only'}
  );

  const selectors =
    selectorQueryResults.type === QueryResultType.SUCCESS
      ? selectorQueryResults.data.schemaFieldSelectors
      : [];
  const selectorsById = selectors.reduce((acc, curr) => {
    acc[curr.id] = curr;
    return acc;
  }, {} as SelectorsById);

  const inaccessible =
    tasksQueryResults.type !== QueryResultType.SUCCESS
      ? []
      : (tasksQueryResults.data.processTasks.filter((task) =>
          isInaccessible(task)
        ) as Array<InaccessibleInfoGraphQL>);

  if (inaccessible.length > 0) {
    return <WorkspaceItemInaccessibleGuard inaccessibleInfo={inaccessible[0]} modelType="process_task" />;
  }

  if (
    selectorQueryResults.type === QueryResultType.SUCCESS &&
    tasksQueryResults.type === QueryResultType.SUCCESS
  ) {
    const tasks = tasksQueryResults.data.processTasks as Array<ProcessTask>;
    return (
      <DirectExecutionModal
        isLoading={false}
        tasks={tasks}
        directExecutionUuid={props.directExecutionUuid}
        onRequestClose={props.onRequestClose}
        selectorsById={selectorsById}
        onTasksOrOutputsChange={props.onTasksOrOutputsChange}
        autopopulateFromTasks={tasks[0]?.schema.outputSchema?.directExecutionModalAutopopulateTaskIds}
      />
    );
  } else if (
    selectorQueryResults.type === QueryResultType.PENDING ||
    tasksQueryResults.type === QueryResultType.PENDING
  ) {
    return (
      <DirectExecutionModal
        isLoading={true}
        onRequestClose={props.onRequestClose}
        onTasksOrOutputsChange={props.onTasksOrOutputsChange}
      />
    );
  } else {
    return (
      <DirectExecutionModalWithError
        onRequestClose={props.onRequestClose}
        errorMessage="There was a problem loading your tasks."
      />
    );
  }
}

const styles = StyleSheet.create({
  stepNavContainer: {
    margin: '0 0 6px 0px',
  },
  modalBody: {
    paddingRight: '8px',
  },
  modalText: {
    margin: '12px 0 8px 0',
  },
});
